"use client";

// Native implementation replaces @radix-ui/react-scroll-area to avoid an
// infinite re-render loop caused by Radix's useComposedRefs creating an
// unstable ref callback on every render (incompatible with React 19's ref
// cleanup semantics). Same root cause as the Switch component replacement.

import * as React from "react";
import { cn } from "@gettactic/helpers";

export type ScrollAreaProps = React.HTMLAttributes<HTMLDivElement> & {
  viewportRef?: React.Ref<HTMLDivElement>;
  viewportClassName?: string;
  scrollBarClassname?: string;
  type?: "auto" | "always" | "scroll" | "hover";
  asChild?: boolean;
  dir?: "ltr" | "rtl";
};

const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    {
      className,
      scrollBarClassname,
      viewportClassName,
      children,
      viewportRef,
      type,
      asChild,
      dir,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      dir={dir}
      {...props}
    >
      <div
        ref={viewportRef}
        className={cn(
          "h-full w-full overflow-auto rounded-[inherit] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-400",
          type === "always" && "[&::-webkit-scrollbar-track]:bg-neutral-100",
          viewportClassName
        )}
      >
        {children}
      </div>
    </div>
  )
);

ScrollArea.displayName = "ScrollArea";

const ScrollBar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    orientation?: "vertical" | "horizontal";
    alwaysVisible?: boolean;
  }
>(({ className, orientation = "vertical", alwaysVisible = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex touch-none select-none transition-colors",
      alwaysVisible ? "bg-neutral-100" : "bg-transparent",
      orientation === "vertical" && "h-full w-1.5",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    )}
    {...props}
  />
));
ScrollBar.displayName = "ScrollBar";

export { ScrollArea, ScrollBar };
